import { useCallback, useMemo } from 'react';

import type { V1Thread } from '@/generated/hooks/rain/models';
import {
  useAutomationCreateThread,
  useAutomationListThreads,
  useAutomationUpdateThread,
} from '@/generated/hooks/rain/v1/automation_scoped/thread_service';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import {
  scopedThreads,
  seededThreadVariables,
} from '@/modules/rain/utils/thread-scope';

import type { IRainThreadListAdapter } from './scope';

/**
 * Automation-scoped thread list for `RainThreadSelector`. The run- and
 * workspace-scoped services expose the same three endpoints, so a peer adapter
 * is all another surface needs to get the same picker.
 */
export function useAutomationThreadListAdapter(
  automationId: string,
  agentId: string,
  /**
   * Thread variables narrowing the agent's threads to one sub-scope, filtering
   * the list and seeding a created thread. Prime's threads are per target, so
   * the step workbench passes `{ target }`: unscoped, the picker would offer
   * another target's conversations and a created thread would never be found
   * again.
   */
  scopeVariables?: Record<string, string>,
): IRainThreadListAdapter {
  const organization = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspace = useUserPreferencesStore(selectCurrentWorkspaceId);

  const {
    data: threadsResponse,
    isLoading,
    refetch,
    isRefetching,
  } = useAutomationListThreads(organization, workspace, automationId, agentId);

  // The service lists every thread for the agent, so the automation scope
  // filters to its own. Same variables `createThread` writes below.
  const threads = useMemo<V1Thread[]>(
    () =>
      scopedThreads(threadsResponse?.data?.threads, {
        automationId,
        ...scopeVariables,
      }),
    [automationId, scopeVariables, threadsResponse],
  );

  const { mutateAsync: createThreadAsync, isPending: isCreating } =
    useAutomationCreateThread();
  const { mutateAsync: updateThreadAsync } = useAutomationUpdateThread();

  const createThread = useCallback(async (): Promise<string | undefined> => {
    const response = await createThreadAsync({
      organization,
      workspace,
      automation: automationId,
      agentId,
      data: {
        variables: seededThreadVariables(organization, workspace, {
          automationId,
          ...scopeVariables,
        }),
      },
    });
    return response.data?.thread_id;
  }, [
    agentId,
    automationId,
    createThreadAsync,
    organization,
    scopeVariables,
    workspace,
  ]);

  const renameThread = useCallback(
    (threadId: string, title: string) =>
      updateThreadAsync({
        organization,
        workspace,
        automation: automationId,
        agentId,
        threadId,
        data: { title },
      }),
    [agentId, automationId, organization, updateThreadAsync, workspace],
  );

  return {
    threads,
    isLoading,
    isRefetching,
    refetch,
    createThread,
    isCreating,
    renameThread,
  };
}
